import knowledgeBase from "../generated/assistantKnowledgeBase.json";

export type AssistantArticle = {
  id: string;
  title: string;
  status: "draft" | "reviewed" | "published" | "disabled";
  questions: string[];
  keywords: string[];
  route: {
    template: string;
    label: string;
  };
  availability: {
    requiresClockStation: boolean;
    requiresMelody: boolean;
  };
  content: string[];
};

export type AssistantSearchContext = {
  currentRoute?: string;
  isClockStation?: boolean;
  hasMelody?: boolean;
};

export type AssistantSearchResult = {
  article: AssistantArticle;
  score: number;
};

const stopWords = new Set([
  "а", "без", "бы", "в", "вам", "вас", "вот", "все", "где", "для", "до",
  "его", "ее", "если", "есть", "же", "за", "и", "из", "или", "как", "ли",
  "мне", "мой", "мы", "на", "над", "не", "но", "о", "об", "он", "она",
  "они", "от", "по", "под", "при", "с", "со", "то", "у", "что", "это", "я"
]);

const aliases = new Map([
  ["вайфай", "wifi"],
  ["вифи", "wifi"],
  ["симка", "sim"],
  ["сим", "sim"],
  ["джипиэс", "gps"],
  ["гпс", "gps"],
  ["нтп", "ntp"],
  ["биглайн", "bigline"]
]);

const tokenPrefixes: Array<[string, string]> = [
  ["настро", "настройка"],
  ["синхрониз", "синхронизация"],
  ["подключ", "подключение"],
  ["перезап", "перезапуск"],
  ["обнов", "обновление"]
];

const normalize = (value: string) => value
  .toLowerCase()
  .replaceAll("ё", "е")
  .replace(/[‐‑‒–—−]/g, "-")
  .replace(/[^a-zа-я0-9+-]+/gi, " ")
  .replace(/\s+/g, " ")
  .trim();

const canonicalize = (token: string) => {
  const alias = aliases.get(token) ?? token;
  const prefixMatch = tokenPrefixes.find(([prefix]) => alias.startsWith(prefix));
  return prefixMatch ? prefixMatch[1] : alias;
};

const tokenize = (value: string) => normalize(value)
  .split(" ")
  .filter((token) => token.length >= 2 && !stopWords.has(token))
  .map(canonicalize);

const tokensAreRelated = (left: string, right: string) => {
  if (left === right) return true;
  if (left.length < 4 || right.length < 4) return false;
  return left.startsWith(right) || right.startsWith(left);
};

const countMatches = (queryTokens: string[], candidateTokens: string[]) => queryTokens.reduce(
  (total, queryToken) => total + (candidateTokens.some((token) => tokensAreRelated(queryToken, token)) ? 1 : 0),
  0
);

const isAvailable = (article: AssistantArticle, context: AssistantSearchContext) => {
  if (context.isClockStation === false && article.availability.requiresClockStation) return false;
  if (context.hasMelody === false && article.availability.requiresMelody) return false;
  return true;
};

export const getAvailableAssistantArticles = (
  context: AssistantSearchContext = {}
): AssistantArticle[] => (knowledgeBase.articles as AssistantArticle[])
  .filter((article) => article.status !== "disabled")
  .filter((article) => isAvailable(article, context));

const scoreArticle = (article: AssistantArticle, query: string, context: AssistantSearchContext) => {
  const normalizedQuery = normalize(query);
  const queryTokens = tokenize(query);
  const title = normalize(article.title);
  const questions = article.questions.map(normalize);
  let score = 0;

  if (title === normalizedQuery) score += 60;
  if (title.includes(normalizedQuery) || normalizedQuery.includes(title)) score += 24;
  if (questions.some((question) => question === normalizedQuery)) score += 70;
  if (questions.some((question) => question.includes(normalizedQuery) || normalizedQuery.includes(question))) score += 28;

  score += countMatches(queryTokens, tokenize(article.title)) * 9;
  score += countMatches(queryTokens, tokenize(article.questions.join(" "))) * 6;
  score += countMatches(queryTokens, tokenize(article.keywords.join(" "))) * 8;
  score += countMatches(queryTokens, tokenize(article.content.join(" "))) * 2;

  if (context.currentRoute && article.route.template.includes(context.currentRoute)) score += 8;
  return score;
};

export const searchAssistantKnowledge = (
  query: string,
  context: AssistantSearchContext = {},
  limit = 3
): AssistantSearchResult[] => {
  if (!query.trim()) return [];

  return getAvailableAssistantArticles(context)
    .map((article) => ({ article, score: scoreArticle(article, query, context) }))
    .filter((result) => result.score >= 8)
    .sort((left, right) => right.score - left.score || left.article.title.localeCompare(right.article.title, "ru"))
    .slice(0, limit);
};
